// NEW: UI_REVAMP
import { Box, Flex, Grid, Input, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useRef } from 'react';
import { SPECIAL_KEYS, useKeypad } from '../../../hooks/useKeypad';
import { useGlobalToast } from '../../../utils/ShowToast';
import SelectionButton from '../../button/SelectionButton';
import { BackspaceIcon } from './icon';

const BLOCK_INPUT_PROPS = {
	inputMode: 'numeric',
	onPaste: (e) => e.preventDefault(),
};

export const Bet3KeypadBase = ({
	slotCount,
	title = 'Specific Numbers',
	onAddToList,
	onManualInteract,
	activeBetType,
	isDisabled,
}) => {
	const isBetTypeEmpty = activeBetType === null;
	const globalShowToast = useGlobalToast();
	const maxDigits = slotCount === 3 ? 1 : 2;
	const {
		slots,
		activeSlot,
		setActiveSlot,
		handleNumberClick,
		handleBackspace,
		onSlotClick,
		handleKeyDown,
	} = useKeypad(slotCount, { maxDigits, onAddToList, onManualInteract });
	const inputRefs = useRef([]);

	useEffect(() => {
		inputRefs.current[activeSlot]?.focus();
	}, [activeSlot, slotCount]);

	useEffect(() => {
		setActiveSlot(0);
	}, [slotCount]);

	const specialKeypadConfig = useMemo(
		() => [
			{ id: SPECIAL_KEYS.EMPTY, content: '', onClick: () => {} },
			{ id: SPECIAL_KEYS.ZERO, content: '0', onClick: () => handleNumberClick('0') },
			{ id: SPECIAL_KEYS.BACKSPACE, content: 'backspace.svg', onClick: handleBackspace },
		],
		[handleNumberClick, handleBackspace],
	);

	const renderSlot = (index) => {
		const isActive = activeSlot === index && !isDisabled && !isBetTypeEmpty;

		return (
			<Input
				key={index}
				ref={(el) => (inputRefs.current[index] = el)}
				value={
					slots[index]
						? maxDigits === 2
							? slots[index].toString().padStart(2, '0')
							: slots[index]
						: ''
				}
				maxLength={2}
				letterSpacing='2px'
				onClick={() => {
					onManualInteract?.();
					onSlotClick(index);
				}}
				onKeyDown={(e) => handleKeyDown(e, index)}
				bgColor={isActive ? '#C7BFFF' : '#DAD4FF'}
				textAlign='center'
				fontWeight='700'
				fontSize='24px'
				cursor={isDisabled || isBetTypeEmpty ? 'not-allowed' : 'default'}
				border={isActive ? '2px solid' : '1px solid'}
				borderColor={isActive ? '#3099ff' : 'transparent'}
				boxShadow={isActive ? '0px 0px 8px rgba(69,124,238,0.5)' : 'none'}
				focusBorderColor={isActive ? '#3099ff' : 'transparent'}
				isReadOnly={isDisabled || isBetTypeEmpty}
				_hover={{}}
				pointerEvents={isDisabled || isBetTypeEmpty ? 'none' : 'auto'}
				{...BLOCK_INPUT_PROPS}
			/>
		);
	};

	const renderNumberButton = (num) => {
		let state;
		if (isDisabled) {
			state = 'disabled';
		} else {
			state = 'default';
		}
		return (
			<SelectionButton
				key={num}
				variant='numberPick'
				onClick={() => {
					if (isBetTypeEmpty) {
						globalShowToast({
							title: 'Please select Straight or Ramble',
							status: 'warning',
							position: 'top',
						});
						return;
					}
					handleNumberClick(String(num));
				}}
				status={state}
			>
				<Text
					fontSize='18px'
					color='inherit'
					fontWeight={slots[activeSlot] === String(num) ? 700 : 600}
				>
					{num}
				</Text>
			</SelectionButton>
		);
	};

	const renderSpecialKey = (key) => {
		let state;
		if (isDisabled) {
			state = 'disabled';
		} else {
			state = 'default';
		}
		return (
			<SelectionButton
				key={key.id}
				variant='numberPick'
				onClick={() => {
					if (isBetTypeEmpty && key.id !== SPECIAL_KEYS.EMPTY) {
						globalShowToast({
							title: 'Please select Straight or Ramble',
							status: 'warning',
							position: 'top',
						});
						return;
					}
					key.onClick?.();
				}}
				status={state}
				cursor={key.id === SPECIAL_KEYS.EMPTY ? 'default !important' : 'poniter'}
			>
				{key.content === 'backspace.svg' ? (
					<BackspaceIcon color='inherit' />
				) : (
					<Text fontSize='18px' color='inherit' fontWeight={700}>
						{key.content}
					</Text>
				)}
			</SelectionButton>
		);
	};

	return (
		<Flex w='100%' h='auto'>
			<Flex direction='column' px={2} pt={1} flex='1'>
				<Text fontWeight={500} fontSize='12px' color='#5E5D61'>
					{title}
				</Text>

				<Flex gap={2}>{Array.from({ length: slotCount }, (_, i) => renderSlot(i))}</Flex>

				<Box
					className='numbers-container'
					flex='1'
					overflowY='auto'
					mt={2}
					pointerEvents={isDisabled ? 'none' : 'auto'}
				>
					<Grid templateColumns='repeat(3, 1fr)' gap={2}>
						{Array.from({ length: 9 }, (_, i) => renderNumberButton(i + 1))}
						{specialKeypadConfig.map(renderSpecialKey)}
					</Grid>
				</Box>
			</Flex>
		</Flex>
	);
};
